import { format } from 'date-fns';
import Cookies from 'js-cookie';
import { isBefore } from 'date-fns';

import Swal, { SweetAlertIcon } from 'sweetalert2';
import fileDownload from 'js-file-download';

export const getOptionsFromEnum = <T extends Record<string, string | number>>(
  enumType: T
): { value: T[keyof T]; label: string }[] => {
  return Object.entries(enumType).map(([key, value]) => ({
    value: value as T[keyof T],
    label: key
  }));
};

export const formatDate = (date: string | number | Date | undefined, formatStr: string = 'MMM d, yyyy hh:mm a') => {
  if (!date) {
    return 'Invalid Date';
  }

  const parsedDate = new Date(date);

  if (isNaN(parsedDate.getTime())) {
    return 'Invalid Date';
  }

  return format(parsedDate, formatStr);
};

export const setCookie = (name: string, value: any) => {
  Cookies.set(name, value, { expires: 7 });
};

export const getCookie = (name: string) => {
  return Cookies.get(name);
};

export const removeCookie = (name: string) => {
  Cookies.remove(name);
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = (error) => reject(error);
  });
};

export const isSubscriptionActive = (subscriptionEndDate?: string | null): boolean => {
  if (!subscriptionEndDate) return false;
  return isBefore(new Date(), subscriptionEndDate);
};

type ConfirmDialogProps = {
  title?: string;
  text?: string;
  icon?: SweetAlertIcon;
  iconColor?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
};

export const confirmDialog = async ({
  title = 'Are you sure?',
  text = 'Do you want to proceed?',
  icon = 'warning',
  iconColor = '',
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  confirmButtonColor = '#4B22A8',
  cancelButtonColor = 'transparent'
}: ConfirmDialogProps) => {
  const result = await Swal.fire({
    title,
    text,
    icon,
    iconColor,
    confirmButtonText,
    cancelButtonText,
    confirmButtonColor,
    cancelButtonColor,
    showCancelButton: true
  });

  return result;
};

export const getUrlInfo = () => {
  const url = window.location.href;
  const protocol = url.split('//')[0];
  const domain = url.split('//')[1].split(':')[0].split('/')[0];

  const domainParts = domain.split('.');
  const hasSubdomain = domainParts.length > 2;
  const subdomain = hasSubdomain ? domainParts.slice(0, -2).join('.') : '';

  return {
    protocol,
    domain,
    subdomain
  };
};

export const handleDownloadByUrl = async (fileUrl: string) => {
  try {
    const response = await fetch(fileUrl, {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    link.setAttribute('download', fileUrl.split('/').pop() || 'downloaded_file');
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

export const fileDownloader = (data: any, filename = 'report.csv') => {
  fileDownload(data, filename);
};

export const formatCurrency = (value: number | undefined): string => {
  if (value == null || isNaN(value)) return '$0.00';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

export const formatDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
  parts.push(`${secs}s`);

  return parts.join(' ');
};

export const formatRevenue = (cents: number) => {
  return `$${(cents / 100).toFixed(2)}`;
};

export const titleCase = (str: string) => {
  return str
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()); // Capitalize each word
};
